'use client';

import { Shimmer } from '@/components/ai-elements/shimmer';
import { cn } from '@/lib/utils';
import { getChromeStrings, isEmbedSurface, useEmbedLocale } from '@/lib/embed-locale';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';



/**
 * Animation configuration constants for the thinking message component
 */
const ANIMATION_CONFIG = {
  /** Container fade-in animation duration */
  fadeIn: { duration: 0.3 },

  /** Pulsing scale animation for the shape */
  pulse: {
    scale: [1, 1.15, 1],
    duration: 1.2,
    repeat: Number.POSITIVE_INFINITY,
    ease: 'easeInOut' as const,
  },

  /** Morphing border radius animation for the shape */
  morph: {
    borderRadius: ['20%', '50%', '20%'],
    duration: 4,
    repeat: Number.POSITIVE_INFINITY,
    ease: 'easeInOut' as const,
  },
};


/**
 * Duration in milliseconds to display status messages before returning to default
 */
const STATUS_DISPLAY_DURATION = 4000;

/**
 * Default message shown when no status message is active
 */
const DEFAULT_MESSAGE = 'TeamMate is working on your request...';

/**
 * Props type for the ThinkingMessage component
 */
type ThinkingMessageProps = {
  /** Custom message text to display instead of the default */
  message?: string;

  /** Additional CSS classes to apply to the container */
  className?: string;
};

/**
 * AnimatedShape Component
 *
 * Renders a small animated shape that pulses and morphs between square and circle.
 * Provides visual feedback that the system is actively processing.
 */
const AnimatedShape = () => (
  <motion.div
    aria-hidden
    className="relative flex size-3 items-center justify-center bg-gradient-to-r from-orange-300 to-orange-500 shadow-sm"
    animate={{
      scale: ANIMATION_CONFIG.pulse.scale,
      borderRadius: ANIMATION_CONFIG.morph.borderRadius,
    }}
    transition={{
      scale: {
        duration: ANIMATION_CONFIG.pulse.duration,
        repeat: ANIMATION_CONFIG.pulse.repeat,
        ease: ANIMATION_CONFIG.pulse.ease,
      },
      borderRadius: {
        duration: ANIMATION_CONFIG.morph.duration,
        repeat: ANIMATION_CONFIG.morph.repeat,
        ease: ANIMATION_CONFIG.morph.ease,
      },
    }}
  />
);

/**
 * ThinkingMessage Component
 *
 * A visually engaging loading indicator that displays while AI is processing a request.
 * Features multiple synchronized animations:
 * - Pulsing and morphing animated shape (square ↔ circle)
 * - Shimmer effect on gradient text
 * - Smooth fade-in transition
 * - Real-time elapsed timer
 * - ESC key interrupt instruction
 *
 * @param props - Component props
 * @param props.message - Custom message text (defaults to "TeamMate is working on your request...")
 * @param props.className - Additional CSS classes for the container
 *
 * @example
 * ```tsx
 * <ThinkingMessage />
 * <ThinkingMessage message="Processing your data..." />
 * <ThinkingMessage className="my-4" />
 * ```
 */
export const ThinkingMessage = ({
  message,
  className,
}: ThinkingMessageProps = {}) => {
  // Localize the "working…" status ONLY in the embed (Arabic bubble etc.); the
  // main app keeps its exact wording.
  const locale = useEmbedLocale();
  const onEmbed = isEmbedSurface();
  const strings = getChromeStrings(locale);
  const localizedDefault = onEmbed ? strings.workingOnRequest : DEFAULT_MESSAGE;
  const localize = (m?: string): string => {
    if (!onEmbed) return m || DEFAULT_MESSAGE;
    // Server-streamed statuses (e.g. "Working…") are English; on the embed we
    // present a single localized "working" label instead of leaking English.
    if (m === 'Stopping response...' || m === 'Stopping response…') {
      return strings.stoppingResponse;
    }
    return localizedDefault;
  };

  /**
   * The message currently being displayed, manages auto-reset to default
   */
  const [displayedMessage, setDisplayedMessage] = useState(localize(message));

  /**
   * Auto-reset status messages to default after STATUS_DISPLAY_DURATION.
   * When a new status message arrives, show it briefly then return to default.
   */
  useEffect(() => {
    if (message && message !== DEFAULT_MESSAGE) {
      setDisplayedMessage(localize(message));

      const timeoutId = setTimeout(() => {
        setDisplayedMessage(localizedDefault);
      }, STATUS_DISPLAY_DURATION);

      return () => clearTimeout(timeoutId);
    } else {
      setDisplayedMessage(localizedDefault);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message, locale, onEmbed]);

  return (
    <motion.div
      className={cn(
        'group/message mx-auto w-full max-w-3xl xl:max-w-4xl px-4 py-4',
        className
      )}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={ANIMATION_CONFIG.fadeIn}
    >
      <div className="flex items-center gap-3" role="status" aria-live="polite">
        <AnimatedShape />
        <Shimmer as="span" className="text-sm font-medium" duration={2} spread={2}>
          {displayedMessage}
        </Shimmer>
      </div>
    </motion.div>
  );
};
